import React, { useCallback, useEffect, useState } from "react";
import { PackageSearch, CheckCircle2, Loader2, RefreshCw, Undo2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";

/**
 * Phase 101dt — Audit & récupération des barquettes "sorties sans transit Conthey".
 *
 * Depuis Phase 101dt, toute sortie d'un distributeur doit transiter par le
 * Stock Conthey (mode alignement, mode comptoir, DLC dépassée). Avant ce
 * changement, plusieurs flux détruisaient les barquettes directement :
 *  - Slot-audit "Aligner sur Nayax" (target < AM) → ADJUST négatif
 *  - Mode Comptoir −1 / saisie qty à la baisse → ADJUST négatif
 *  - Retrait DLC unitaire / en masse → OUT sec
 *
 * Ce panneau retrouve ces barquettes "perdues" sur une fenêtre donnée et
 * les recrée au Stock Conthey avec leurs métadonnées d'origine (lot_number,
 * DLC, product) pour que l'opératrice puisse les redispatcher.
 */
export default function AdjustAuditPanel() {
  const [hours, setHours] = useState(24);  // Défaut : dernières 24 heures
  const [items, setItems] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lastReport, setLastReport] = useState(null);
  const confirm = useConfirm();

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/adjust-audit", { params: { hours } });
      setItems(data.items || []);
      setTotalQty(data.total_qty || 0);
      setSelected(new Set((data.items || []).map((g) => g.key)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur audit ADJUST/OUT");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const toggleKey = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runRestore = async () => {
    if (selected.size === 0) {
      toast.error("Sélectionne au moins un groupe à restaurer");
      return;
    }
    const selectedItems = items.filter((g) => selected.has(g.key));
    const totalToRestore = selectedItems.reduce((acc, g) => acc + g.total_qty_lost, 0);
    const ok = await confirm({
      title: "Récupérer les barquettes au Stock Conthey ?",
      description: `Recréer/incrémenter ${selected.size} lot(s) totalisant ${totalToRestore} barquette(s) au Stock Conthey avec leurs lot_number, DLC, product d'origine.\n\nLes mouvements historiques (ADJUST/OUT) restent visibles (traçabilité HACCP). Un mouvement IN de compensation sera créé pour chaque groupe.\n\nCette action est irréversible.`,
      confirmText: `Récupérer ${totalToRestore} barquette(s)`,
      cancelText: "Annuler",
    });
    if (!ok) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/adjust-restore", {
        hours,
        keys: Array.from(selected),
      });
      setLastReport(data);
      toast.success(`✅ ${data.restored_count} groupe(s) restauré(s) · ${data.total_qty_restored} barquette(s) au Stock Conthey`);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur restauration");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="border border-[#C3D1E8] bg-[#F0F5FB] rounded-lg" data-testid="adjust-audit-panel">
      <div className="p-4 border-b border-[#C3D1E8]">
        <div className="flex items-center gap-2 mb-1">
          <PackageSearch className="w-5 h-5 text-[#2F5A8C]" />
          <h3 className="font-semibold text-[#1A3D5A]">Récupérer les barquettes &laquo;sorties sans transit Conthey&raquo; (Phase 101dt)</h3>
        </div>
        <p className="text-xs text-[#2F5A8C] leading-relaxed">
          Détecte les barquettes qui ont été retirées d&apos;un distributeur avant Phase 101dt (par alignement Nayax, mode comptoir −1, saisie qty à la baisse, retrait DLC) sans transiter par le Stock Conthey.
          Depuis Phase 101dt le comportement est corrigé, mais les barquettes déjà &laquo;perdues&raquo; peuvent être recréées ici avec leurs métadonnées d&apos;origine pour un redispatch.
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs mb-1 block">Fenêtre (heures)</Label>
            <Input
              type="number"
              min="1"
              max="720"
              value={hours}
              onChange={(e) => setHours(Math.max(1, Number(e.target.value) || 24))}
              className="h-9 w-24"
              data-testid="adjust-audit-hours"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAudit}
            disabled={loading}
            className="h-9"
            data-testid="adjust-audit-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Actualiser
          </Button>
          <div className="flex-1" />
          <div className="text-xs text-[#1A3D5A]">
            <span className="font-semibold">{items.length}</span> groupe(s) ·
            <span className="font-semibold ml-1 text-[#C05C47]">{totalQty}</span> barquette(s) sans transit
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="bg-[#EAF4EE] border border-[#5A8F76] rounded-md p-3 text-sm text-[#1F4E3D] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Aucune barquette sortie sans transit Conthey sur cette fenêtre — rien à récupérer.
          </div>
        )}

        {items.length > 0 && (
          <>
            <div className="border border-[#E5E5E0] rounded-md bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                  <tr>
                    <th className="text-left px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={selected.size === items.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(items.map((g) => g.key)) : new Set())}
                        data-testid="adjust-audit-select-all"
                      />
                    </th>
                    <th className="text-left px-2 py-2">Lot</th>
                    <th className="text-left px-2 py-2">Produit</th>
                    <th className="text-left px-2 py-2">Source (distributeur · slot)</th>
                    <th className="text-center px-2 py-2 w-16">Qté</th>
                    <th className="text-left px-2 py-2">DLC</th>
                    <th className="text-left px-2 py-2">Origine</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((g) => (
                    <tr
                      key={g.key}
                      className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]"
                      data-testid={`adjust-audit-row-${g.lot_number}`}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(g.key)}
                          onChange={() => toggleKey(g.key)}
                          data-testid={`adjust-audit-check-${g.lot_number}`}
                        />
                      </td>
                      <td className="px-2 py-2 font-mono">{g.lot_number || "—"}</td>
                      <td className="px-2 py-2">
                        <div>{g.product_name}</div>
                        <div className="text-[10px] text-[#6B6E6B] font-mono">{g.product_sku}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium">{g.distributor_name}</div>
                        <div className="text-[10px] text-[#6B6E6B]">slot {g.slot_number ?? "—"}</div>
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-bold text-[#C05C47]">
                        {g.total_qty_lost}
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px]">
                        {g.dlc || "—"}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-[#6B6E6B]">
                        {g.movements.length} mouvement(s)
                        <div>du {new Date(g.first_at).toLocaleDateString("fr-CH")} au {new Date(g.last_at).toLocaleDateString("fr-CH")}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <Badge variant="outline" className="bg-white">
                {selected.size} groupe(s) sélectionné(s)
              </Badge>
              <Button
                onClick={runRestore}
                disabled={restoring || selected.size === 0}
                className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                data-testid="adjust-audit-restore-btn"
              >
                {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
                Récupérer au Stock Conthey
              </Button>
            </div>
          </>
        )}

        {lastReport && (
          <div className="mt-4 border border-[#5A8F76] bg-[#EAF4EE] rounded-md p-3 text-xs" data-testid="adjust-audit-report">
            <div className="font-semibold text-[#1F4E3D] mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Rapport de récupération
            </div>
            <div className="grid grid-cols-2 gap-2 text-[#1F4E3D]">
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Groupes restaurés</span>
                <div className="font-mono text-lg">{lastReport.restored_count}</div>
              </div>
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Barquettes totales</span>
                <div className="font-mono text-lg">{lastReport.total_qty_restored}</div>
              </div>
            </div>
            {lastReport.warnings && lastReport.warnings.length > 0 && (
              <div className="mt-2 border-t border-[#5A8F76] pt-2 text-[#C05C47]">
                <div className="font-semibold">Avertissements :</div>
                {lastReport.warnings.map((w, idx) => (
                  <div key={idx} className="text-[11px]">• {w}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
